import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { COLORS } from '../theme/designTokens';
import type { TopBarTheme } from '../utils/topBarSectionColor';

export interface TopBarAppearance {
  bg: string;
  theme: TopBarTheme;
}

const MENU_EXIT_MS = 800;

interface MenuContextValue {
  menuOpen: boolean;
  /** true while menu is open OR exit animation is still running */
  menuVisible: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  toggleMenu: () => void;
  appearance: TopBarAppearance;
  setAppearance: (appearance: TopBarAppearance) => void;
  contentScrollRef: React.RefObject<HTMLDivElement | null>;
  registerContentScrollRef: (ref: HTMLDivElement | null) => void;
}

const defaultAppearance: TopBarAppearance = {
  bg: COLORS.bg,
  theme: 'light',
};

const MenuContext = createContext<MenuContextValue | null>(null);

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [appearance, setAppearance] = useState<TopBarAppearance>(defaultAppearance);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const exitTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const registerContentScrollRef = useCallback((el: HTMLDivElement | null) => {
    contentScrollRef.current = el;
  }, []);

  const openMenu = useCallback(() => {
    clearTimeout(exitTimer.current);
    setMenuOpen(true);
    setMenuVisible(true);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    exitTimer.current = setTimeout(() => {
      setMenuVisible(false);
      window.dispatchEvent(new Event('scroll'));
    }, MENU_EXIT_MS + 50);
  }, []);

  useEffect(() => () => clearTimeout(exitTimer.current), []);

  const toggleMenu = useCallback(() => {
    if (menuOpen) closeMenu();
    else openMenu();
  }, [menuOpen, closeMenu, openMenu]);

  const value = useMemo(
    () => ({
      menuOpen,
      menuVisible,
      openMenu,
      closeMenu,
      toggleMenu,
      appearance,
      setAppearance,
      contentScrollRef,
      registerContentScrollRef,
    }),
    [
      menuOpen,
      menuVisible,
      openMenu,
      closeMenu,
      toggleMenu,
      appearance,
      registerContentScrollRef,
    ],
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};

export function useMenu() {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('useMenu must be used within MenuProvider');
  return ctx;
}
